/**
 * Coalesces duplicate cart reads.
 *
 * The cart is touched by six scripts (cart.js, cart-drawer.js, the drawer
 * snippet, complementary recommendations, gift cleanup, reward cleanup) with 20
 * separate `fetch('/cart.js')` call sites between them. They all react to the
 * same events, so a single add-to-cart measured 14 identical /cart.js requests
 * plus duplicate cart-drawer section renders — about six seconds of chatter.
 *
 * The cart can only change through a mutation request from this page, so
 * between mutations a cart read is guaranteed to return what the last one did.
 * That makes caching correct rather than merely fast: this holds the response
 * until the next /cart/add|change|update|clear (or a short ceiling, so a change
 * made in another tab cannot pin a stale cart indefinitely), and joins requests
 * already in flight instead of issuing a second one.
 *
 * Must load before the other cart scripts. Nothing else has to change.
 */
(function () {
  if (window.__cartRequestCache) return;
  window.__cartRequestCache = true;

  var TTL = 10000; // ceiling for changes this page did not make (e.g. another tab)
  var MUTATION = /\/cart\/(add|change|update|clear)/i;
  var CACHEABLE = /\/cart\.js(\?|$)|[?&]section_id=cart-drawer/i;

  var inflight = {};
  var cache = {};
  var nativeFetch = window.fetch.bind(window);

  function invalidate() {
    inflight = {};
    cache = {};
  }

  // Buffer the body once, then hand every caller its own Response. Cloning a
  // Response that another caller has already read would give them an empty body.
  function record(response) {
    return response.text().then(function (text) {
      return {
        at: Date.now(),
        text: text,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    });
  }

  function replay(rec) {
    var body = rec.status === 204 || rec.status === 205 || rec.status === 304 ? null : rec.text;
    return new Response(body, { status: rec.status, statusText: rec.statusText, headers: rec.headers });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    if (MUTATION.test(url)) {
      invalidate();
      return nativeFetch.apply(null, arguments).then(function (response) {
        invalidate(); // the response itself is the new source of truth
        return response;
      });
    }

    if (method !== 'GET' || !CACHEABLE.test(url)) {
      return nativeFetch.apply(null, arguments);
    }

    var hit = cache[url];
    if (hit && Date.now() - hit.at < TTL) {
      return Promise.resolve(replay(hit));
    }

    if (inflight[url]) {
      return inflight[url].then(replay);
    }

    var pending = nativeFetch
      .apply(null, arguments)
      .then(record)
      .then(function (rec) {
        cache[url] = rec;
        delete inflight[url];
        return rec;
      })
      .catch(function (error) {
        delete inflight[url];
        throw error;
      });

    inflight[url] = pending;
    return pending.then(replay);
  };

  // A cart mutation submitted as a form (not fetch) still changes the cart.
  document.addEventListener('submit', function (event) {
    var action = event.target && event.target.getAttribute('action');
    if (action && MUTATION.test(action)) invalidate();
  }, true);

  window.addEventListener('pageshow', invalidate);
})();
